import type { components } from './generated/api';

export type CloudPrincipal = components['schemas']['MeResponse'];
export type CloudWorkspace = components['schemas']['WorkspaceResponse'];
export type CreateWorkspaceInput = components['schemas']['WorkspaceInput'];
export type CloudBudget = components['schemas']['BudgetResponse'];
export type CreateBudgetInput = components['schemas']['NameInput'];
export type CloudBackup = components['schemas']['BackupMetadata'];
export type CreateBackupInput = components['schemas']['BackupCreateInput'];
export type BackupUpload = components['schemas']['BackupUploadResponse'];
export type BackupDownload = components['schemas']['BackupDownloadResponse'];
export type TransferTicket = components['schemas']['TransferTicket'];

export type CloudApiErrorKind =
  | 'forbidden'
  | 'invalid-response'
  | 'network'
  | 'request-failed'
  | 'unauthorized';

export class CloudApiError extends Error {
  constructor(
    public readonly kind: CloudApiErrorKind,
    public readonly status: number,
    message: string,
    public readonly backendCode: string | null = null,
  ) {
    super(message);
    this.name = 'CloudApiError';
  }
}

type Fetch = typeof fetch;

export type CloudApiClient = ReturnType<typeof createCloudApiClient>;

export function createCloudApiClient(
  apiBaseUrl: string,
  getAccessToken: () => Promise<string>,
  fetcher: Fetch = fetch,
) {
  async function request(
    path: string,
    init: RequestInit = {},
  ): Promise<unknown> {
    const accessToken = await getAccessToken();
    const headers = new Headers(init.headers);
    headers.set('Accept', 'application/json');
    headers.set('Authorization', `Bearer ${accessToken}`);
    let response: Response;
    try {
      response = await fetcher(`${apiBaseUrl}${path}`, {
        ...init,
        headers,
      });
    } catch (error) {
      throw new CloudApiError(
        'network',
        0,
        error instanceof Error ? error.message : 'Cloud API is unavailable.',
      );
    }

    const payload: unknown = await response.json().catch(() => null);
    if (response.status === 401) {
      throw new CloudApiError(
        'unauthorized',
        response.status,
        'Cloud session is invalid or expired.',
      );
    }
    if (response.status === 403) {
      const backendError = readBackendError(payload);
      throw new CloudApiError(
        'forbidden',
        response.status,
        backendError?.detail ?? 'Cloud operation is not permitted.',
        backendError?.code ?? null,
      );
    }
    if (!response.ok) {
      const backendError = readBackendError(payload);
      throw new CloudApiError(
        'request-failed',
        response.status,
        backendError?.detail ??
          `Cloud API failed with HTTP ${response.status}.`,
        backendError?.code ?? null,
      );
    }
    return payload;
  }

  async function getMe(): Promise<CloudPrincipal> {
    const payload = await request('/api/v1/me');
    if (!isCloudPrincipal(payload)) throw invalidResponse('/api/v1/me');
    return payload;
  }

  async function listWorkspaces(): Promise<readonly CloudWorkspace[]> {
    const workspaces: CloudWorkspace[] = [];
    let cursor: string | null = null;
    do {
      const query = new URLSearchParams({ limit: '100' });
      if (cursor !== null) query.set('cursor', cursor);
      const payload = await request(`/api/v1/workspaces?${query}`);
      if (!isWorkspacePage(payload)) {
        throw invalidResponse('/api/v1/workspaces');
      }
      workspaces.push(...payload.items);
      cursor = payload.next_cursor;
    } while (cursor !== null);
    return workspaces;
  }

  async function createWorkspace(
    input: CreateWorkspaceInput,
  ): Promise<CloudWorkspace> {
    const payload = await request('/api/v1/workspaces', {
      body: JSON.stringify(input),
      headers: { 'Content-Type': 'application/json' },
      method: 'POST',
    });
    if (!isCloudWorkspace(payload)) {
      throw invalidResponse('/api/v1/workspaces');
    }
    return payload;
  }

  async function listBudgets(
    workspaceId: string,
  ): Promise<readonly CloudBudget[]> {
    const budgets: CloudBudget[] = [];
    let cursor: string | null = null;
    do {
      const query = new URLSearchParams({ limit: '100' });
      if (cursor !== null) query.set('cursor', cursor);
      const payload = await request(
        `/api/v1/workspaces/${segment(workspaceId)}/budgets?${query}`,
      );
      if (!isBudgetPage(payload)) throw invalidResponse('budgets');
      budgets.push(...payload.items);
      cursor = payload.next_cursor;
    } while (cursor !== null);
    return budgets;
  }

  async function createBudget(
    workspaceId: string,
    input: CreateBudgetInput,
  ): Promise<CloudBudget> {
    const payload = await request(
      `/api/v1/workspaces/${segment(workspaceId)}/budgets`,
      {
        body: JSON.stringify(input),
        headers: { 'Content-Type': 'application/json' },
        method: 'POST',
      },
    );
    if (!isCloudBudget(payload)) throw invalidResponse('budget');
    return payload;
  }

  async function listBackups(
    workspaceId: string,
    budgetId: string,
  ): Promise<readonly CloudBackup[]> {
    const backups: CloudBackup[] = [];
    let cursor: string | null = null;
    do {
      const query = new URLSearchParams({ limit: '100' });
      if (cursor !== null) query.set('cursor', cursor);
      const payload = await request(
        `${backupCollectionPath(workspaceId, budgetId)}?${query}`,
      );
      if (!isBackupPage(payload)) throw invalidResponse('backups');
      backups.push(...payload.items);
      cursor = payload.next_cursor;
    } while (cursor !== null);
    return backups;
  }

  async function createBackup(
    workspaceId: string,
    budgetId: string,
    input: CreateBackupInput,
  ): Promise<BackupUpload> {
    const payload = await request(backupCollectionPath(workspaceId, budgetId), {
      body: JSON.stringify(input),
      headers: { 'Content-Type': 'application/json' },
      method: 'POST',
    });
    if (!isBackupUpload(payload)) throw invalidResponse('backup upload');
    return payload;
  }

  async function getBackupDownload(
    workspaceId: string,
    budgetId: string,
    backupId: string,
  ): Promise<BackupDownload> {
    const payload = await request(
      `${backupCollectionPath(workspaceId, budgetId)}/${segment(backupId)}/download`,
    );
    if (!isBackupDownload(payload)) throw invalidResponse('backup download');
    return payload;
  }

  return {
    createBackup,
    createBudget,
    createWorkspace,
    getBackupDownload,
    getMe,
    listBackups,
    listBudgets,
    listWorkspaces,
  } as const;
}

function invalidResponse(path: string): CloudApiError {
  return new CloudApiError(
    'invalid-response',
    200,
    `Cloud API returned an invalid response for ${path}.`,
  );
}

function isCloudPrincipal(value: unknown): value is CloudPrincipal {
  return (
    isRecord(value) &&
    typeof value.subject === 'string' &&
    (value.email === null || typeof value.email === 'string')
  );
}

function isCloudWorkspace(value: unknown): value is CloudWorkspace {
  return (
    isRecord(value) &&
    typeof value.id === 'string' &&
    typeof value.name === 'string' &&
    typeof value.created_at === 'string' &&
    typeof value.created_by_subject === 'string'
  );
}

function isWorkspacePage(
  value: unknown,
): value is components['schemas']['WorkspaceListResponse'] {
  return (
    isRecord(value) &&
    Array.isArray(value.items) &&
    value.items.every(isCloudWorkspace) &&
    (value.next_cursor === null || typeof value.next_cursor === 'string')
  );
}

function isCloudBudget(value: unknown): value is CloudBudget {
  return (
    isRecord(value) &&
    typeof value.id === 'string' &&
    typeof value.workspace_id === 'string' &&
    typeof value.name === 'string' &&
    typeof value.created_at === 'string' &&
    typeof value.created_by_subject === 'string' &&
    (value.status === 'active' || value.status === 'archived') &&
    typeof value.revision === 'number'
  );
}

function isBudgetPage(
  value: unknown,
): value is components['schemas']['BudgetListResponse'] {
  return (
    isRecord(value) &&
    Array.isArray(value.items) &&
    value.items.every(isCloudBudget) &&
    (value.next_cursor === null || typeof value.next_cursor === 'string')
  );
}

function isCloudBackup(value: unknown): value is CloudBackup {
  return (
    isRecord(value) &&
    typeof value.id === 'string' &&
    typeof value.workspace_id === 'string' &&
    typeof value.budget_id === 'string' &&
    typeof value.owner_subject === 'string' &&
    typeof value.created_at === 'string' &&
    typeof value.size_bytes === 'number' &&
    typeof value.checksum_sha256 === 'string'
  );
}

function isBackupPage(
  value: unknown,
): value is components['schemas']['BackupListResponse'] {
  return (
    isRecord(value) &&
    Array.isArray(value.items) &&
    value.items.every(isCloudBackup) &&
    (value.next_cursor === null || typeof value.next_cursor === 'string')
  );
}

function isTransferTicket(value: unknown): boolean {
  return (
    isRecord(value) &&
    typeof value.method === 'string' &&
    typeof value.url === 'string' &&
    typeof value.expires_in === 'number' &&
    isRecord(value.headers) &&
    Object.values(value.headers).every(header => typeof header === 'string')
  );
}

function isBackupUpload(value: unknown): value is BackupUpload {
  return (
    isRecord(value) &&
    isCloudBackup(value.backup) &&
    isTransferTicket(value.upload)
  );
}

function isBackupDownload(value: unknown): value is BackupDownload {
  return (
    isRecord(value) &&
    isCloudBackup(value.backup) &&
    isTransferTicket(value.download)
  );
}

function backupCollectionPath(workspaceId: string, budgetId: string): string {
  return `/api/v1/workspaces/${segment(workspaceId)}/budgets/${segment(budgetId)}/backups`;
}

function segment(value: string): string {
  return encodeURIComponent(value);
}

function readBackendError(
  value: unknown,
): Readonly<{ code: string | null; detail: string }> | null {
  if (!isRecord(value) || typeof value.detail !== 'string') return null;
  return {
    code: typeof value.code === 'string' ? value.code : null,
    detail: value.detail,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}
